import { useState, useEffect } from 'react'
import { useAuthStore } from '@/core/stores/auth'
import {
  useTeamMembers, usePendingInvites, useInviteUser,
  useRevokeInvite, useChangeRole, useToggleActive,
  useDeleteUser, useChangePassword,
  type TeamMember, type PendingInvite,
} from '@/shared/hooks/useTeam'
import {
  Users, Mail, Shield, ChevronDown, Trash2, UserMinus,
  UserCheck, X, Loader2, Key, Clock, Check, AlertCircle,
  Plus, Pencil, UserPlus,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import api from '@/shared/lib/api'

const roleConfig: Record<string, { label: string; color: string; description: string }> = {
  admin: { label: 'Admin', color: 'bg-gold-light text-gold-dark', description: 'Accès complet : équipe, billing, modules' },
  manager: { label: 'Manager', color: 'bg-blue-100 text-blue-700', description: 'Utiliser les modules, créer et modifier' },
  viewer: { label: 'Viewer', color: 'bg-paper-warm text-ink', description: 'Lecture seule : dashboards et exports' },
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Jamais'
  const diff = Date.now() - new Date(dateStr).getTime()
  const days = Math.floor(diff / 86400000)
  if (days === 0) return "Aujourd'hui"
  if (days === 1) return 'Hier'
  if (days < 30) return `Il y a ${days}j`
  return `Il y a ${Math.floor(days / 30)} mois`
}

// ── Types ────────────────────────────────────────────────────

interface TeamInfo {
  id: string
  name: string
  description: string | null
  member_count: number
}

interface TeamDetail {
  id: string
  name: string
  description: string | null
  members: Array<{
    id: string
    user_id: string
    email: string
    full_name: string | null
    role: string
    job_title: string | null
    joined_at: string | null
  }>
}

// ── Create Team Modal ─────────────────────────────────────────

function CreateTeamModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleCreate = async () => {
    if (!name.trim()) return
    setLoading(true)
    setError('')
    try {
      await api.post('/teams', { name: name.trim(), description: description.trim() || null })
      onCreated()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la création')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-md p-6 animate-slide-up">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-ink">Nouvelle équipe</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper-warm"><X size={16} className="text-ink-faint" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Nom de l'équipe</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Marketing, SEO, Support..."
              className="w-full px-3 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Description (optionnel)</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Équipe en charge de..."
              className="w-full px-3 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold" />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-ink-muted hover:bg-paper-warm rounded-lg">Annuler</button>
            <button onClick={handleCreate} disabled={loading || !name.trim()}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg disabled:opacity-50">
              {loading && <Loader2 size={14} className="animate-spin" />} Créer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Rename Team Modal ─────────────────────────────────────────

function RenameTeamModal({ team, onClose, onRenamed }: { team: TeamInfo; onClose: () => void; onRenamed: () => void }) {
  const [name, setName] = useState(team.name)
  const [description, setDescription] = useState(team.description || '')
  const [loading, setLoading] = useState(false)

  const handleSave = async () => {
    if (!name.trim()) return
    setLoading(true)
    try {
      await api.patch(`/teams/${team.id}`, { name: name.trim(), description: description.trim() || null })
      onRenamed()
      onClose()
    } catch { /* */ }
    finally { setLoading(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-md p-6 animate-slide-up">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-ink">Modifier l'équipe</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper-warm"><X size={16} className="text-ink-faint" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Nom</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1">Description</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2.5 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-ink-muted hover:bg-paper-warm rounded-lg">Annuler</button>
            <button onClick={handleSave} disabled={loading || !name.trim()}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg disabled:opacity-50">
              {loading && <Loader2 size={14} className="animate-spin" />} Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Add Member to Team Modal ──────────────────────────────────

function AddToTeamModal({ teamId, teamName, existingMemberIds, onClose, onAdded }: {
  teamId: string; teamName: string; existingMemberIds: string[]; onClose: () => void; onAdded: () => void
}) {
  const { data: allMembers } = useTeamMembers()
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [role, setRole] = useState('member')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const availableUsers = (allMembers || []).filter(
    (m: TeamMember) => !existingMemberIds.includes(m.id)
  )

  const handleAdd = async () => {
    if (!selectedUser) return
    setLoading(true)
    setError('')
    try {
      await api.post(`/teams/${teamId}/members`, { user_id: selectedUser, role })
      onAdded()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-md p-6 animate-slide-up">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-ink">Ajouter à {teamName}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper-warm"><X size={16} className="text-ink-faint" /></button>
        </div>
        <div className="space-y-4">
          {availableUsers.length === 0 ? (
            <p className="text-sm text-ink-muted text-center py-4">Tous les membres du workspace font déjà partie de cette équipe.</p>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1.5">Membre</label>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {availableUsers.map((u: TeamMember) => (
                    <label key={u.id}
                      className={cn('flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                        selectedUser === u.id ? 'border-gold bg-gold-light/30' : 'border-ink-border hover:bg-paper')}>
                      <input type="radio" name="user" value={u.id}
                        checked={selectedUser === u.id}
                        onChange={() => setSelectedUser(u.id)}
                        className="accent-gold" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink truncate">{u.full_name || u.email}</p>
                        <p className="text-[10px] text-ink-faint">{u.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1.5">Rôle dans l'équipe</label>
                <select value={role} onChange={(e) => setRole(e.target.value)}
                  className="w-full px-3 py-2 border border-ink-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gold/50">
                  <option value="member">Membre</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-ink-muted hover:bg-paper-warm rounded-lg">Annuler</button>
            {availableUsers.length > 0 && (
              <button onClick={handleAdd} disabled={loading || !selectedUser}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg disabled:opacity-50">
                {loading && <Loader2 size={14} className="animate-spin" />} Ajouter
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Invite Modal (with team selection) ───────────────────────

function InviteModal({ open, onClose, teams }: { open: boolean; onClose: () => void; teams: TeamInfo[] }) {
  const invite = useInviteUser()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('manager')
  const [teamId, setTeamId] = useState<string>('')
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (open && teams.length === 1) setTeamId(teams[0].id)
  }, [open, teams])

  if (!open) return null

  const handleSubmit = () => {
    if (!email.trim()) return
    invite.mutate(
      { email: email.trim(), role, team_id: teamId || undefined } as any,
      { onSuccess: (data: any) => { setSuccess(data.invite_url); setEmail('') } },
    )
  }

  const handleClose = () => { onClose(); setSuccess(null); setEmail(''); setTeamId('') }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-md p-6 animate-slide-up">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-ink">Inviter un membre</h3>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-paper-warm"><X size={18} className="text-ink-faint" /></button>
        </div>

        {success ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <Check size={16} className="text-emerald-600" />
              <p className="text-sm text-emerald-700">Invitation envoyée !</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1">Lien d'invitation</label>
              <div className="flex items-center gap-2">
                <input type="text" readOnly value={success}
                  className="flex-1 px-3 py-2 text-xs font-mono border border-ink-border rounded-lg bg-paper" />
                <button onClick={() => navigator.clipboard.writeText(success)}
                  className="px-3 py-2 text-xs font-medium text-brick bg-gold-light rounded-lg">Copier</button>
              </div>
            </div>
            <button onClick={handleClose}
              className="w-full px-4 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg">Fermer</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="collegue@entreprise.com"
                className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-2">Rôle</label>
              <div className="space-y-2">
                {Object.entries(roleConfig).map(([key, cfg]) => (
                  <label key={key}
                    className={cn('flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                      role === key ? 'border-gold bg-gold-light/30' : 'border-ink-border hover:bg-paper')}>
                    <input type="radio" name="role" value={key} checked={role === key}
                      onChange={() => setRole(key)} className="accent-gold" />
                    <div>
                      <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full', cfg.color)}>{cfg.label}</span>
                      <p className="text-xs text-ink-muted mt-0.5">{cfg.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            {/* Team selection */}
            {teams.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-2">
                  Équipe {teams.length === 1 && <span className="text-ink-faint">(par défaut)</span>}
                </label>
                <div className="space-y-1.5">
                  {teams.map((team) => (
                    <label key={team.id}
                      className={cn('flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                        teamId === team.id ? 'border-gold bg-gold-light/30' : 'border-ink-border hover:bg-paper')}>
                      <input type="radio" name="team" value={team.id}
                        checked={teamId === team.id}
                        onChange={() => setTeamId(team.id)}
                        className="accent-gold" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-ink">{team.name}</p>
                        <p className="text-[10px] text-ink-faint">{team.member_count} membre(s)</p>
                      </div>
                      <Users size={14} className="text-ink-faint" />
                    </label>
                  ))}
                </div>
              </div>
            )}
            {invite.isError && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={14} className="text-red-500" />
                <p className="text-xs text-red-700">{(invite.error as Error)?.message || "Erreur lors de l'envoi"}</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={handleClose} className="px-4 py-2 text-sm text-ink hover:bg-paper-warm rounded-lg">Annuler</button>
              <button onClick={handleSubmit} disabled={!email.trim() || invite.isPending}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg disabled:opacity-50">
                {invite.isPending && <Loader2 size={14} className="animate-spin" />}
                Envoyer l'invitation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Role Dropdown ────────────────────────────────────────────

function RoleDropdown({ currentRole, userId, isSelf }: { currentRole: string; userId: string; isSelf: boolean }) {
  const [open, setOpen] = useState(false)
  const changeRole = useChangeRole()

  if (isSelf) {
    const cfg = roleConfig[currentRole] ?? roleConfig.viewer
    return <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full', cfg.color)}>{cfg.label} (vous)</span>
  }

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className={cn('inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full cursor-pointer hover:ring-2 hover:ring-gold',
          roleConfig[currentRole]?.color ?? roleConfig.viewer.color)}>
        {roleConfig[currentRole]?.label ?? currentRole}
        <ChevronDown size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute z-30 top-full mt-1 left-0 bg-white border border-ink-border rounded-card shadow-elevated py-1 min-w-[140px] animate-fade-in">
            {Object.entries(roleConfig).map(([key, cfg]) => (
              <button key={key}
                onClick={() => { changeRole.mutate({ userId, role: key }); setOpen(false) }}
                className={cn('w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-paper',
                  currentRole === key && 'font-bold')}>
                <span className={cn('w-2 h-2 rounded-full', cfg.color.split(' ')[0])} />
                {cfg.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ── Delete Confirm Modal ─────────────────────────────────────

function DeleteMemberModal({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const deleteUser = useDeleteUser()
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-sm p-6 animate-slide-up">
        <h3 className="text-lg font-semibold text-ink mb-2">Supprimer ce membre ?</h3>
        <p className="text-sm text-ink-muted mb-6">
          <strong>{member.full_name || member.email}</strong> sera définitivement supprimé.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-ink hover:bg-paper-warm rounded-lg">Annuler</button>
          <button onClick={() => { deleteUser.mutate(member.id); onClose() }}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg">Supprimer</button>
        </div>
      </div>
    </div>
  )
}

// ── Change Password Section ──────────────────────────────────

function ChangePasswordSection() {
  const user = useAuthStore((s) => s.user)
  const changePassword = useChangePassword()
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' })
  const [success, setSuccess] = useState(false)

  if (user?.provider !== 'email') return null

  const canSubmit = form.current_password && form.new_password.length >= 8 && form.new_password === form.confirm

  const handleSubmit = () => {
    if (!canSubmit) return
    changePassword.mutate(
      { current_password: form.current_password, new_password: form.new_password },
      { onSuccess: () => { setForm({ current_password: '', new_password: '', confirm: '' }); setSuccess(true); setTimeout(() => setSuccess(false), 3000) } },
    )
  }

  return (
    <div className="bg-white rounded-card border border-ink-border p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><Key size={20} className="text-amber-600" /></div>
        <div>
          <h3 className="text-base font-semibold text-ink">Mot de passe</h3>
          <p className="text-sm text-ink-muted">Modifiez votre mot de passe de connexion.</p>
        </div>
        {success && <Check size={16} className="text-emerald-500 ml-auto" />}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
        <div>
          <label className="block text-xs font-medium text-ink-muted mb-1">Mot de passe actuel</label>
          <input type="password" value={form.current_password} onChange={(e) => setForm((p) => ({ ...p, current_password: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold" />
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-muted mb-1">Nouveau mot de passe</label>
          <input type="password" value={form.new_password} onChange={(e) => setForm((p) => ({ ...p, new_password: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold" />
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-muted mb-1">Confirmer</label>
          <input type="password" value={form.confirm} onChange={(e) => setForm((p) => ({ ...p, confirm: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold" />
        </div>
      </div>
      {form.new_password && form.confirm && form.new_password !== form.confirm && (
        <p className="text-xs text-red-500 mt-2">Les mots de passe ne correspondent pas.</p>
      )}
      {changePassword.isError && (
        <p className="text-xs text-red-500 mt-2">{(changePassword.error as Error)?.message || 'Erreur'}</p>
      )}
      <button onClick={handleSubmit} disabled={!canSubmit || changePassword.isPending}
        className="mt-3 px-4 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg disabled:opacity-50">
        {changePassword.isPending ? 'Modification…' : 'Changer le mot de passe'}
      </button>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ══════════════════════════════════════════════════════════════

export function TeamPage() {
  const currentUser = useAuthStore((s) => s.user)
  const { data: members, isLoading } = useTeamMembers()
  const { data: invites } = usePendingInvites()
  const toggleActive = useToggleActive()
  const revokeInvite = useRevokeInvite()

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'owner'

  const [showInvite, setShowInvite] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<TeamMember | null>(null)

  // Teams state
  const [teams, setTeams] = useState<TeamInfo[]>([])
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null)
  const [teamDetail, setTeamDetail] = useState<TeamDetail | null>(null)
  const [teamsLoading, setTeamsLoading] = useState(true)
  const [teamDetailLoading, setTeamDetailLoading] = useState(false)
  const [showCreateTeam, setShowCreateTeam] = useState(false)
  const [renameTarget, setRenameTarget] = useState<TeamInfo | null>(null)
  const [showAddToTeam, setShowAddToTeam] = useState(false)
  const [deletingTeam, setDeletingTeam] = useState(false)

  const fetchTeams = async () => {
    try {
      const { data } = await api.get('/teams')
      const list = data.data || []
      setTeams(list)
      if (list.length > 0 && !selectedTeam) setSelectedTeam(list[0].id)
    } catch { /* */ }
    finally { setTeamsLoading(false) }
  }

  const fetchTeamDetail = async (teamId: string) => {
    setTeamDetailLoading(true)
    try {
      const { data } = await api.get(`/teams/${teamId}`)
      setTeamDetail(data.data)
    } catch { /* */ }
    finally { setTeamDetailLoading(false) }
  }

  useEffect(() => { fetchTeams() }, [])
  useEffect(() => { if (selectedTeam) fetchTeamDetail(selectedTeam) }, [selectedTeam])

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return
    const team = teams.find(t => t.id === selectedTeam)
    if (!confirm(`Supprimer l'équipe "${team?.name}" ? Les membres ne seront pas supprimés du workspace.`)) return
    setDeletingTeam(true)
    try {
      await api.delete(`/teams/${selectedTeam}`)
      setSelectedTeam(null)
      setTeamDetail(null)
      fetchTeams()
    } catch { /* */ }
    finally { setDeletingTeam(false) }
  }

  const handleRemoveFromTeam = async (userId: string) => {
    if (!selectedTeam) return
    try {
      await api.delete(`/teams/${selectedTeam}/members/${userId}`)
      fetchTeamDetail(selectedTeam)
      fetchTeams()
    } catch { /* */ }
  }

  const selectedTeamInfo = teams.find(t => t.id === selectedTeam)
  const existingMemberIds = teamDetail?.members.map(m => m.user_id) || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Équipe</h1>
          <p className="text-ink-muted mt-1">Gérez les membres et les accès de votre workspace.</p>
        </div>
        <button onClick={() => setShowInvite(true)}
          className="flex items-center gap-2 rounded-card bg-brick px-4 py-2.5 text-sm font-semibold text-white hover:bg-brick-hover transition-colors">
          <Mail size={16} /> Inviter un membre
        </button>
      </div>

      {/* ══ TEAMS MANAGEMENT (admin/owner only) ═══════════════ */}
      {isAdmin && (
        <div className="bg-white rounded-card border border-ink-border overflow-hidden">
          {/* Teams header */}
          <div className="px-5 py-4 border-b border-ink-border flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
              <Users size={16} className="text-gold-dark" /> Équipes <span className="text-ink-faint font-normal">({teams.length})</span>
            </h3>
            <button onClick={() => setShowCreateTeam(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg transition-colors">
              <Plus size={14} /> Nouvelle équipe
            </button>
          </div>

          {teamsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-ink-faint" />
            </div>
          ) : teams.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-sm text-ink-muted">Aucune équipe créée</p>
              <p className="text-xs text-ink-faint mt-1">Créez votre première équipe pour organiser vos collaborateurs.</p>
            </div>
          ) : (
            <div className="flex divide-x divide-ink-border/30">
              {/* Team list (left) */}
              <div className="w-56 flex-shrink-0">
                {teams.map((team) => (
                  <button key={team.id} onClick={() => setSelectedTeam(team.id)}
                    className={cn('w-full flex items-center gap-2 px-4 py-3 text-left transition-colors',
                      selectedTeam === team.id ? 'bg-gold-light/40 border-r-2 border-gold' : 'hover:bg-paper')}>
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm truncate', selectedTeam === team.id ? 'font-semibold text-ink' : 'text-ink-muted')}>{team.name}</p>
                      <p className="text-[10px] text-ink-faint">{team.member_count} membre(s)</p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Team detail (right) */}
              <div className="flex-1 min-w-0">
                {selectedTeam && selectedTeamInfo ? (
                  <>
                    {/* Team detail header */}
                    <div className="px-5 py-3 bg-paper/50 flex items-center justify-between border-b border-ink-border/30">
                      <div>
                        <p className="text-sm font-semibold text-ink">{selectedTeamInfo.name}</p>
                        {selectedTeamInfo.description && (
                          <p className="text-[11px] text-ink-faint">{selectedTeamInfo.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setShowAddToTeam(true)}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-ink bg-paper-warm hover:bg-ink-border/30 rounded-lg transition-colors">
                          <UserPlus size={12} /> Ajouter
                        </button>
                        <button onClick={() => setRenameTarget(selectedTeamInfo)}
                          className="p-1.5 text-ink-faint hover:text-ink hover:bg-paper-warm rounded-lg transition-colors" title="Modifier">
                          <Pencil size={13} />
                        </button>
                        <button onClick={handleDeleteTeam} disabled={deletingTeam}
                          className="p-1.5 text-ink-faint hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer l'équipe">
                          {deletingTeam ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                        </button>
                      </div>
                    </div>

                    {/* Members list */}
                    {teamDetailLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 size={18} className="animate-spin text-ink-faint" />
                      </div>
                    ) : teamDetail?.members.length === 0 ? (
                      <div className="px-5 py-8 text-center">
                        <p className="text-sm text-ink-faint">Aucun membre dans cette équipe</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-ink-border/20">
                        {teamDetail?.members.map((m) => (
                          <div key={m.id} className="flex items-center gap-3 px-5 py-2.5">
                            <div className="w-7 h-7 rounded-full bg-gold-light flex items-center justify-center text-[11px] font-bold text-brick">
                              {(m.full_name || m.email)[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-ink truncate">{m.full_name || m.email}</p>
                                <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded-full',
                                  m.role === 'owner' ? 'bg-gold-light text-gold-dark'
                                    : m.role === 'admin' ? 'bg-gold-light text-gold-dark'
                                    : m.role === 'manager' ? 'bg-blue-100 text-blue-700'
                                    : 'bg-paper-warm text-ink-muted')}>
                                  {m.role}
                                </span>
                              </div>
                              <p className="text-[10px] text-ink-faint">{m.email}{m.job_title ? ` — ${m.job_title}` : ''}</p>
                            </div>
                            <button onClick={() => handleRemoveFromTeam(m.user_id)}
                              className="p-1.5 text-ink-faint hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Retirer de l'équipe">
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="px-5 py-8 text-center text-sm text-ink-faint">
                    Sélectionnez une équipe
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ ALL WORKSPACE MEMBERS ═════════════════════════════ */}
      <div className="bg-white rounded-card border border-ink-border overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">
            Tous les membres <span className="text-ink-faint font-normal">({members?.length ?? 0})</span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-border bg-paper">
                <th className="text-left px-4 py-3 font-medium text-ink-muted">Membre</th>
                <th className="text-left px-4 py-3 font-medium text-ink-muted">Rôle</th>
                <th className="text-left px-4 py-3 font-medium text-ink-muted">Connexion</th>
                <th className="text-left px-4 py-3 font-medium text-ink-muted">Dernière activité</th>
                <th className="text-center px-4 py-3 font-medium text-ink-muted">Statut</th>
                <th className="px-4 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-border/30">
              {isLoading ? Array.from({ length: 3 }).map((_, i) => (
                <tr key={i} className="animate-pulse">{Array.from({ length: 6 }).map((_, j) => (
                  <td key={j} className="px-4 py-3"><div className="h-4 bg-paper-warm rounded w-3/4" /></td>
                ))}</tr>
              )) : members?.map((m: TeamMember) => {
                const isSelf = m.id === currentUser?.id
                return (
                  <tr key={m.id} className={cn('hover:bg-paper', !m.is_active && 'opacity-50')}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gold-light flex items-center justify-center text-brick text-xs font-semibold">
                          {(m.full_name || m.email)[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-ink">{m.full_name || '—'}</p>
                          <p className="text-xs text-ink-faint">{m.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <RoleDropdown currentRole={m.role} userId={m.id} isSelf={isSelf} />
                    </td>
                    <td className="px-4 py-3 text-xs text-ink-muted capitalize">{m.provider}</td>
                    <td className="px-4 py-3 text-xs text-ink-muted">{timeAgo(m.last_login)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={cn('text-[11px] px-2 py-0.5 rounded-full font-medium',
                        m.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700')}>
                        {m.is_active ? 'Actif' : 'Désactivé'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {!isSelf && isAdmin && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => toggleActive.mutate(m.id)}
                            className={cn('p-1.5 rounded-lg transition-colors',
                              m.is_active ? 'text-amber-500 hover:bg-amber-50' : 'text-emerald-500 hover:bg-emerald-50')}
                            title={m.is_active ? 'Désactiver' : 'Réactiver'}>
                            {m.is_active ? <UserMinus size={14} /> : <UserCheck size={14} />}
                          </button>
                          <button onClick={() => setDeleteTarget(m)}
                            className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50"
                            title="Supprimer">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Invites */}
      {invites && invites.length > 0 && (
        <div className="bg-white rounded-card border border-ink-border overflow-hidden">
          <div className="px-5 py-4 border-b border-ink-border">
            <h3 className="text-sm font-semibold text-ink">
              Invitations en attente <span className="text-ink-faint font-normal">({invites.length})</span>
            </h3>
          </div>
          <div className="divide-y divide-ink-border/30">
            {invites.map((inv: PendingInvite) => (
              <div key={inv.id} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-paper-warm flex items-center justify-center">
                    <Mail size={14} className="text-ink-faint" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink">{inv.email}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded-full',
                        roleConfig[inv.role]?.color ?? roleConfig.viewer.color)}>
                        {roleConfig[inv.role]?.label ?? inv.role}
                      </span>
                      <span className="text-[10px] text-ink-faint flex items-center gap-0.5">
                        <Clock size={10} /> Expire {timeAgo(inv.expires_at)}
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={() => revokeInvite.mutate(inv.id)}
                  className="text-xs text-red-500 hover:text-red-700 font-medium">Révoquer</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Roles Legend */}
      <div className="bg-white rounded-card border border-ink-border p-5">
        <h3 className="text-sm font-semibold text-ink mb-3">Rôles & permissions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(roleConfig).map(([key, cfg]) => (
            <div key={key} className="rounded-lg border border-ink-border p-3">
              <div className="flex items-center gap-2 mb-1">
                <Shield size={14} className="text-ink-faint" />
                <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full', cfg.color)}>{cfg.label}</span>
              </div>
              <p className="text-xs text-ink-muted">{cfg.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Change Password */}
      <ChangePasswordSection />

      {/* Modals */}
      <InviteModal open={showInvite} onClose={() => setShowInvite(false)} teams={teams} />
      {deleteTarget && <DeleteMemberModal member={deleteTarget} onClose={() => setDeleteTarget(null)} />}
      {showCreateTeam && <CreateTeamModal onClose={() => setShowCreateTeam(false)} onCreated={fetchTeams} />}
      {renameTarget && <RenameTeamModal team={renameTarget} onClose={() => setRenameTarget(null)} onRenamed={() => { fetchTeams(); if (selectedTeam) fetchTeamDetail(selectedTeam) }} />}
      {showAddToTeam && selectedTeam && selectedTeamInfo && (
        <AddToTeamModal
          teamId={selectedTeam}
          teamName={selectedTeamInfo.name}
          existingMemberIds={existingMemberIds}
          onClose={() => setShowAddToTeam(false)}
          onAdded={() => { fetchTeamDetail(selectedTeam); fetchTeams() }}
        />
      )}
    </div>
  )
}
